import { Button, TextInput, Typography } from '@neo4j-ndl/react';
import { Square2StackIconOutline } from '@neo4j-ndl/react/icons';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import {
  ACTION,
  consoleApi,
  getApiError,
  getErrorMessage,
  useActiveOrg,
  useNotificationActions,
  usePermissions,
} from '@nx/state';
import { isNotNullish, validateYup } from '@nx/stdlib';
import { Page } from '@nx/ui';
import { ControlPanelNew } from '@nx/ui/src/control-panel';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import * as yup from 'yup';

import { ConfirmDisableGdsSessionModal, EnableGdsSessionsModal } from './gds-sessions';
import { ModifyGdsSessionsLimitsControlPanel } from './gds-sessions/modify-limits-modal';

const logger = createLogger(APP_SCOPE.framework);

export const updateOrganizationNameSchema = yup.object({
  name: yup
    .string()
    .min(3, 'Organization name must be longer than 3 characters')
    .max(255, 'Organization name can be no longer than 255 characters')
    .required('Organization name cannot be empty'),
});

export const validate = (data: { name: string }, { onlyRequired = false }: { onlyRequired?: boolean } = {}) =>
  validateYup(updateOrganizationNameSchema, data, onlyRequired);

export interface SeamlessConfigState {
  projects: Record<string, boolean>;
  instances: Record<string, boolean>;
}

export function Settings() {
  const activeOrg = useActiveOrg();
  const { addNotification } = useNotificationActions();
  const { isAllowed } = usePermissions();
  const allowEditOrg = isAllowed(ACTION.UPDATE, `organizations/${activeOrg.id}`);

  // Using state for more responsive UI
  const [copilotEnabled, setCopilotEnabled] = useState(false);

  const [updateOrg] = consoleApi.useUpdateOrganizationMutation();

  const handleUpdateOrganizationName = (name: string) => {
    updateOrg({ name: name.trim(), organizationId: activeOrg.id })
      .unwrap()
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        if (isNotNullish(error.message)) {
          logger.error(error.message);
        }
        addNotification({
          type: 'danger',
          title: 'Failed to update organization name',
          description: getErrorMessage(error),
          timeout: 5000,
        });
      });
  };

  const handleUpdateCopilotEnabled = () => {
    const currentlyEnabled = copilotEnabled;
    setCopilotEnabled(!currentlyEnabled);
    updateOrg({ copilotEnabled: !currentlyEnabled, organizationId: activeOrg.id })
      .unwrap()
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        if (isNotNullish(error.message)) {
          logger.error(error.message);
        }
        addNotification({
          type: 'danger',
          title: `Failed to ${currentlyEnabled ? 'disable' : 'enable'} copilots`,
          description: getErrorMessage(error),
          timeout: 5000,
        });
        setCopilotEnabled(currentlyEnabled);
      });
  };

  const [showEnableGdsSessionsModal, setShowEnableGdsSessionsModal] = useState(false);
  const [showDisableGdsSessionsModal, setShowDisableGdsSessionsModal] = useState(false);

  useEffect(() => {
    setCopilotEnabled(activeOrg.copilotEnabled);
  }, [activeOrg]);

  const [organizationNameEditValue, setOrganizationNameEditValue] = useState(activeOrg.displayName);

  return (
    <Page>
      <Typography variant="h2">Organization settings</Typography>

      <ControlPanelNew>
        <ControlPanelNew.Content title="General settings">
          <div className="flex flex-col gap-y-4">
            {allowEditOrg ? (
              <TextInput
                value={organizationNameEditValue}
                label="Organization name"
                isFluid
                className="w-full max-w-[480px]"
                onChange={(e) => setOrganizationNameEditValue(e.target.value)}
              />
            ) : (
              <h5 className="ml-1">{activeOrg.displayName}</h5>
            )}
            <TextInput
              className="text-neutral-text-weaker w-full max-w-[480px]"
              value={activeOrg.id}
              label="Organization ID"
              isFluid
              rightElement={
                <Button
                  size="small"
                  color="neutral"
                  fill="outlined"
                  onClick={() => {
                    void navigator.clipboard.writeText(activeOrg.id);
                  }}
                >
                  <Square2StackIconOutline className="size-4" />
                  Copy
                </Button>
              }
              htmlAttributes={{
                readOnly: true,
              }}
            />
          </div>
        </ControlPanelNew.Content>
        <ControlPanelNew.Footer>
          <Button
            size="small"
            color="neutral"
            fill="outlined"
            isDisabled={organizationNameEditValue === activeOrg.displayName}
            onClick={() => setOrganizationNameEditValue(activeOrg.displayName)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            fill="filled"
            isDisabled={organizationNameEditValue === activeOrg.displayName}
            onClick={() => handleUpdateOrganizationName(organizationNameEditValue)}
          >
            Save
          </Button>
        </ControlPanelNew.Footer>
      </ControlPanelNew>

      <ControlPanelNew>
        <ControlPanelNew.Content title="Preferences">
          <ControlPanelNew.Switch
            label="Generative AI assistance"
            description="Enable generative AI features that assist users in exploring and querying graphs."
            onChange={handleUpdateCopilotEnabled}
            isDisabled={!allowEditOrg}
            isChecked={copilotEnabled}
          />
        </ControlPanelNew.Content>
      </ControlPanelNew>

      <ModifyGdsSessionsLimitsControlPanel
        onSwitch={(newState) => {
          if (newState) {
            setShowEnableGdsSessionsModal(true);
          } else {
            setShowDisableGdsSessionsModal(true);
          }
        }}
      />

      {showEnableGdsSessionsModal && <EnableGdsSessionsModal onClose={() => setShowEnableGdsSessionsModal(false)} />}
      {showDisableGdsSessionsModal && (
        <ConfirmDisableGdsSessionModal onClose={() => setShowDisableGdsSessionsModal(false)} />
      )}
    </Page>
  );
}
